<script setup lang="ts">
  import { useGetAccount } from "@/api/account/account"
  import { MyAccountRoute } from "@/constants/routes/myAccount.ts"
  import { aggregateQueries } from "@/lib/aggregateQueries"
  import { useGetAccountId } from "@/lib/useGetAccountId"
  import { formatCurrency } from "@/lib/utils"
  import { computed } from "vue"
  import ErrorMessage from "@/components/ErrorMessage.vue"
  import { Button } from "@/components/ui/button"
  import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
  } from "@/components/ui/card"
  import { Link } from "@/components/ui/link"

  import BalanceCardSkeleton from "./BalanceCardSkeleton.vue"

  const accountIdQuery = useGetAccountId()
  const accountQuery = useGetAccount(accountIdQuery.accountId)

  const { isError, isSuccess, isLoading } = aggregateQueries([
    accountIdQuery,
    accountQuery,
  ])

  const balanceValue = computed(() => accountQuery.data.value?.balance)

  const formattedBalance = computed(() => {
    if (balanceValue.value === undefined) return ""

    // Reverse sign in front of balance value as BE returns negative for credit and positive for debit
    return formatCurrency(balanceValue.value === 0 ? 0 : -balanceValue.value)
  })

  const balanceType = computed(() => {
    if (!balanceValue.value) return ""
    return balanceValue.value < 0 ? "in credit" : "in debit"
  })

  const makePaymentLink = MyAccountRoute.cardPayment
</script>

<template>
  <BalanceCardSkeleton v-if="isLoading" />
  <Card v-else data-testid="balance-card">
    <CardHeader>
      <CardTitle>Your Balance</CardTitle>
    </CardHeader>
    <CardContent>
      <ErrorMessage v-if="isError" />
      <p v-else class="flex items-baseline" data-testid="balance-display">
        <span class="h2" data-testid="balance-amount">{{
          formattedBalance
        }}</span>
        <span class="body-text ml-2" data-testid="balance-type">{{
          balanceType
        }}</span>
      </p>
    </CardContent>
    <CardFooter>
      <Button variant="tonal" :as-child="isSuccess" :disabled="!isSuccess">
        <span v-if="!isSuccess">Make a payment</span>
        <Link v-else :to="makePaymentLink">Make a payment</Link>
      </Button>
    </CardFooter>
  </Card>
</template>
